import type { AlertColor } from "@mui/material";
import { Alert, Slide, Snackbar } from "@mui/material";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerSnackbar } from "../HTTPClient";
import { registerLoyaltySnackbar } from "../HTTPClientLoyalty";

export type SnackbarContextProps = {
  snackbarShowMessage: (message: string, severity?: AlertColor, duration?: number) => void;
  defaultError: (error?: unknown) => void;
};

type SnackbarMessage = {
  id: string;
  message: string;
  severity: AlertColor;
  duration: number;
};

const SnackbarContext = createContext<SnackbarContextProps | null>(null);

const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<SnackbarMessage[]>([]);
  const { t } = useTranslation();

  const showMessage = (message: string, severity: AlertColor = "success", duration = 10000) => {
    const id = Date.now().toString();
    setMessages((prevMessages) => [...prevMessages, { id, message, severity, duration }]);
  };

  const handleClose = (id: string) => {
    setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== id));
  };

  // Enregistrer la fonction de snackbar pour qu'elle soit accessible globalement
  useEffect(() => {
    registerSnackbar(showMessage);
    registerLoyaltySnackbar(showMessage);
    // Pas besoin de cleanup car registerSnackbar est idempotent
  }, []);

  // Prefer the server's own error/message (e.g. the exact SMSPartner reason)
  // over the generic fallback. `.catch(defaultError)` passes the error along.
  const serverErrorMessage = (error: unknown): string | undefined => {
    const data = (error as { response?: { data?: { error?: unknown; message?: unknown } } })
      ?.response?.data;
    const message = data?.error ?? data?.message;
    return typeof message === "string" && message ? message : undefined;
  };

  const contextValue: SnackbarContextProps = {
    snackbarShowMessage: showMessage,
    defaultError: (error?: unknown) =>
      showMessage(serverErrorMessage(error) ?? t("defaultError"), "error"),
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      {messages.map(({ id, message, severity, duration }) => (
        <Snackbar
          key={id}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          autoHideDuration={duration}
          open={true}
          onClose={() => handleClose(id)}
          TransitionComponent={Slide}
        >
          <Alert variant="filled" onClose={() => handleClose(id)} severity={severity}>
            {message}
          </Alert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  );
};

const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

export { SnackbarProvider, useSnackbar };
